<template>
    <div class="main">
        <div id="Nav" class="left">
            <div class="back"><button @click="goBack()" class="button icon fastic-circle-left" :aria-label="localizer.get('App.Common.PageBack')"></button></div>
            <div class="content">
                <h1 class="maintitle s60">{{ localizer.get("Account.AccountRegister.Title") }}</h1>
                <div class="desc">{{ localizer.get("Account.AccountRegister.Desc") }}</div>
                <div class="buttons">
                    <div class="cta secondary s16" data-color="white"><a :href="routes.Account_User_Login + (returnUrl ? '?returnurl=' + returnUrl : '')" class="button">{{ localizer.get("Account.Login.LoginTitle") }}</a></div>
                    <template v-if="options && options.externalProviders.length !== 0 && !isFromNewsletter">
                        <form :action="routes.Account_External_ExternalLogin + (returnUrl ? '?returnurl=' + returnUrl : '')" method="post" class="external">
                            <fieldset>
                                <div class="or">{{ localizer.get("App.Common.Or") }}</div>
                                <div v-for="(provider, index) in options.externalProviders" :key="index" class="cta secondary s16 icon" data-color="white"><button type="submit" name="provider" :value="provider.name" class="button"><img :src="'/images/' + provider.name + '.svg'" width="20" height="20" class="icon" :alt="provider.name">Accedi con {{ provider.name }}</button></div>
                            </fieldset>
                            <input type="hidden" name="__RequestVerificationToken" :value="formHelper.getRequestVerificationToken()" />
                        </form>
                    </template>
                </div>
            </div>
            <app-footer></app-footer>
        </div>
        <main id="Main" class="right">
            <div class="logo" data-color="cyan50">
                <a :href="routes.Home"><img src="/images/logo.svg" width="125" height="35" :alt="localizer.get('App.Common.Beghelli')" /></a>
            </div>
            <div v-if="options" @keydown.enter.prevent.stop="submitForm()" class="content">
                <div class="intro">
                    <h2 class="maintitle s36">{{ localizer.get("Account.Login.CreateProfile") }}</h2>
                </div>
                <div ref="form" class="form">
                    <fieldset :disabled="isLoading">
                        <div v-if="options.isUserName" class="item">
                            <label for="UserName">{{ localizer.get("Account.Common.UserName") }}</label>
                            <input type="text" id="UserName" v-model.lazy="formData.userName" :class="[{ 'input-validation-error': formHelper.isControlValidationError('UserName', validationErrors) }, 'input w300']" />
                        </div>
                        <div v-show="!isFromNewsletter" class="item">
                            <label for="Email">{{ localizer.get("App.Form.Email") }}</label>
                            <input type="email" id="Email" :placeholder="localizer.get('App.Form.EmailPlaceholder')" v-model.lazy="formData.email" :class="[{ 'input-validation-error': formHelper.isControlValidationError('Email', validationErrors) }, 'input w300']" />
                        </div>
                        <div v-show="options.isEmailCheck" class="item">
                            <label for="EmailCheck">{{ localizer.get("Account.Common.EmailCheck") }}</label>
                            <input type="email" id="EmailCheck" :placeholder="localizer.get('App.Form.EmailPlaceholder')" v-model.lazy="formData.emailCheck" :class="[{ 'input-validation-error': formHelper.isControlValidationError('EmailCheck', validationErrors) }, 'input w300']" />
                        </div>
                        <div v-show="isEmailInUse()" class="item">
                            <div class="label" data-link>
                                {{ localizer.get("Account.Common.Email_InUse_RetrieveText") }} <a :href="routes.Account_User_PasswordRemind" class="link">{{ localizer.get("Account.Common.Email_InUse_RetrieveLink") }}</a>
                            </div>
                        </div>
                        <div class="item">
                            <label for="Password">{{ localizer.get("Account.Common.Password") }}</label>
                            <div class="custominput" data-type="password">
                                <input type="password" :aria-label="localizer.get('Account.Common.Password')" id="Password" v-model.lazy="formData.password" :class="[{ 'input-validation-error input-validation-error-update': formHelper.isControlValidationError('Password', validationErrors) }, 'input w300']" />
                                <button :class="[{ 'fastic-eye-show': isHidden, 'fastic-eye-hide': !isHidden }, 'showhide']" aria-label="Mostra password" @click="formHelper.showPassword('Password'), showPasswordIcon()"></button>
                            </div>
                        </div>
                        <div v-show="options.isPasswordCheck" class="item">
                            <label for="PasswordCheck">{{ localizer.get("Account.Common.PasswordCheck") }}</label>
                            <div class="custominput" data-type="password">
                                <input type="password" :aria-label="localizer.get('App.Form.PasswordCheck')" id="PasswordCheck" v-model.lazy="formData.passwordCheck" :class="[{ 'input-validation-error': formHelper.isControlValidationError('PasswordCheck', validationErrors) }, 'input w300']" />
                                <button :class="[{ 'fastic-eye-show': isHiddenCheck, 'fastic-eye-hide': !isHiddenCheck }, 'showhide']" aria-label="Mostra password" @click="formHelper.showPassword('PasswordCheck'), showPasswordCheckIcon()"></button>
                            </div>
                        </div>
                        <div v-show="!isFromNewsletter" class="item" data-label="top">
                            <label for="ProfileSegment">{{ localizer.get("App.Common.AddFunctionalities") }}</label>
                            <select id="ProfileSegment" v-model="formData.profileSegment" :class="[{ 'input-validation-error': formHelper.isControlValidationError('ProfileSegment', validationErrors) }, 'input w460']">
                                <option :value="null">{{ localizer.get("App.Form.Select") }}</option>
                                <option v-for="(profileSegment, index) in profileSegments" :value="profileSegment.value" :key="index">{{ profileSegment.title }}</option>
                            </select>
                        </div>
                    </fieldset>
                    <fieldset :disabled="isLoading">
                        <div class="item" data-type="list">
                            <div class="checkbox">
                                <input type="checkbox" id="IsNewsletterSubscription" v-model="formData.isNewsletterSubscription">
                                <label for="IsNewsletterSubscription">{{ localizer.get('App.Form.NewsletterSubscription') }}</label>
                            </div>
                        </div>
                        <div class="desc small" v-html="localizer.get('App.Form.PrivacyA', routes.UrlPrivacyPolicy)"></div>
                        <div v-if="validationErrors.length !== 0" class="validation-summary-errors">
                            <ul>
                                <li v-for="(validationError, index) in validationErrors" :key="index">{{ validationError.message }}</li>
                            </ul>
                        </div>
                        <div class="cta"><button :class="[{ 'loading': isLoading }, 'button']" @click="submitForm()"><template v-if="!isLoading">{{ localizer.get('App.Form.CreateProfile') }}</template></button></div>
                    </fieldset>
                    <div class="desc small grecaptcha-terms" v-html="localizer.get('App.Support.Captcha_Terms')"></div>
                </div>
            </div>
        </main>
    </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount } from "vue";
import Axios from "axios";
import FormHelper from "@Scripts/Common/FormHelper";
import Localizer from "@Scripts/Common/Localizer";

const props = defineProps<{
    returnUrl: string;
    recaptchaSiteKey: string;
    email: string;
    profileSegment: string;
    urlPrivacyPolicy: string;
}>();

const localizer = Localizer;
const formHelper = FormHelper;
const routes = (window as any).App.routes;

const isLoading = ref(false);
const isSuccess = ref(false);
const isFromNewsletter = ref(false);
const options = ref<any>(null);
const profileSegments = ref<any[]>([]);
const formData = ref<any>({ profileSegment: null, isNewsletterSubscription: false });
const validationErrors = ref<any[]>([]);
const isHidden = ref(true);
const isHiddenCheck = ref(true);

onBeforeMount(async () => {
    options.value = (await Axios.post(routes.Account_User_GetOptions, null, { headers: { "RequestVerificationToken": FormHelper.getRequestVerificationToken() } })).data;
    profileSegments.value = options.value.profileSegments;
    if (props.email && props.profileSegment) {
        formData.value.email = props.email;
        formData.value.profileSegment = props.profileSegment;
        isFromNewsletter.value = true;
    }
});

function isEmailInUse(): boolean {
    return (validationErrors.value.length !== 0 && validationErrors.value.filter((x: any) => x.message === localizer.get("Account.Common.Email_InUse")).length > 0);
}

function showPasswordIcon() {
    isHidden.value = !isHidden.value;
}

function showPasswordCheckIcon() {
    isHiddenCheck.value = !isHiddenCheck.value;
}

function goBack() {
    props.returnUrl ? (window.location.href = props.returnUrl) : history.back();
}

async function submitForm() {
    FormHelper.removeFocus();
    validationErrors.value = [];
    isLoading.value = true;

    (window as any).grecaptcha.enterprise.ready(async () => {
        const token = await (window as any).grecaptcha.enterprise.execute(props.recaptchaSiteKey, { action: "Submit" });
        try {
            let result = ( await Axios.post(routes.Account_User_Register, formData.value, { headers: { "RequestVerificationToken": FormHelper.getRequestVerificationToken(), RecaptchaVerificationToken: token }, })).data;
            isSuccess.value = true;
            window.location.href = result.redirectUrl ? result.redirectUrl : props.returnUrl ? props.returnUrl : "/";
        } catch (error: any) {
            formData.value.password = null;
            formData.value.passwordCheck = null;
            if (error.response && error.response.status === 400) {
                validationErrors.value = FormHelper.getValidationErrors(error.response.data);
            }
        }
        isLoading.value = false;
    });
}
</script>